/**
 * Minimal fetch wrapper for the public hosted-checkout endpoints.
 *
 * All calls go through `/api/v1/public/sessions/...` on the same
 * origin the web app is served from. The checkout JWT is carried
 * in the `?token=` query string on every call because that is the
 * contract the CheckoutJwtGuard enforces.
 *
 * The web app uses a Vite dev proxy or runs on the same host as the
 * server at production time; for local development the server and
 * web live on separate ports (server: 4000, web: 4100) and the web
 * app talks to the server directly via absolute URLs. Controlled by
 * VITE_API_BASE_URL — defaults to http://localhost:4000.
 */

const API_BASE_URL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
  'http://localhost:4000';

export interface PublicSession {
  sessionId: string;
  status:
    | 'pending'
    | 'completed'
    | 'refunded'
    | 'partially_refunded'
    | 'expired'
    | 'failed';
  provider: string;
  productName: string;
  grossAmount: string;
  currency: string;
  expiresAt: string;
  paidAt: string | null;
  successUrl: string | null;
  cancelUrl: string | null;
  clientTheme: {
    clientName: string;
  };
}

export interface PublicSessionStatus {
  status: PublicSession['status'];
  paidAt: string | null;
}

export interface ApiError {
  code: string;
  message: string;
  requestId: string;
}

export class PublicApiError extends Error {
  constructor(
    readonly status: number,
    readonly payload: ApiError,
  ) {
    super(payload.message);
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers ?? {}),
    },
  });
  const body = (await response.json().catch(() => null)) as
    | { data?: T; error?: ApiError }
    | null;
  if (!response.ok) {
    const err = body?.error ?? {
      code: `http_${response.status}`,
      message: 'Request failed',
      requestId: 'req_unknown',
    };
    throw new PublicApiError(response.status, err);
  }
  return (body?.data as T) ?? ({} as T);
}

export async function getPublicSession(
  sessionId: string,
  token: string,
): Promise<PublicSession> {
  return request<PublicSession>(
    `/api/v1/public/sessions/${encodeURIComponent(sessionId)}?token=${encodeURIComponent(token)}`,
  );
}

export async function getPublicSessionStatus(
  sessionId: string,
  token: string,
): Promise<PublicSessionStatus> {
  return request<PublicSessionStatus>(
    `/api/v1/public/sessions/${encodeURIComponent(sessionId)}/status?token=${encodeURIComponent(token)}`,
  );
}

export async function mockCompleteSession(
  sessionId: string,
  token: string,
): Promise<{
  sessionId: string;
  status: string;
  paidAt: string | null;
  successUrl: string | null;
}> {
  return request(
    `/api/v1/public/sessions/${encodeURIComponent(sessionId)}/mock-complete?token=${encodeURIComponent(token)}`,
    {
      method: 'POST',
      body: '{}',
    },
  );
}

// ---------------------------------------------------------------------------
// Toss confirm
// ---------------------------------------------------------------------------

export interface ConfirmTossPaymentResult {
  readonly sessionId: string;
  readonly status: string;
  readonly paidAt: string | null;
  readonly successUrl: string | null;
}

/**
 * Known server error codes from the confirm endpoint mapped to stable
 * UI-friendly reason slugs. Used by TossSuccessPage to build
 * /cancel?reason=... URLs. Unknown codes fall through to the generic
 * error flow.
 */
export const CONFIRM_REJECTED_REASONS = {
  confirm_rejected: 'confirm_rejected',
  provider_unavailable: 'provider_unavailable',
  session_invalid_state: 'already_processed',
  session_expired: 'expired',
} as const satisfies Record<string, string>;

export type ConfirmRejectedReason =
  (typeof CONFIRM_REJECTED_REASONS)[keyof typeof CONFIRM_REJECTED_REASONS];

/**
 * Call POST /api/v1/public/sessions/:id/confirm with the Toss
 * redirect parameters. The server validates params, calls Toss REST
 * confirm, and transitions the session to completed atomically.
 *
 * @throws PublicApiError on 4xx/5xx. Caller inspects
 *   err.payload.code and maps via CONFIRM_REJECTED_REASONS.
 */
export async function confirmTossPayment(
  sessionId: string,
  token: string,
  body: {
    paymentKey: string;
    orderId: string;
    amount: number;
  },
): Promise<ConfirmTossPaymentResult> {
  return request<ConfirmTossPaymentResult>(
    `/api/v1/public/sessions/${encodeURIComponent(sessionId)}/confirm?token=${encodeURIComponent(token)}`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );
}
